import {
  DuviSharedAdminCard as Card,
  DuviSharedAdminCardHeader as CardHeader,
  DuviSharedAdminCardBody as CardBody,
  DuviSharedAdminKpiCard as KpiCard,
  DuviSharedAdminDataTable as DataTable,
  DuviSharedAdminButton as Button,
  DuviSharedAdminIcon as Icon,
  DuviSharedAdminAvatar as Avatar,
  DuviSharedAdminBadge as Badge,
  DuviSharedAdminStatusPill as StatusPill,
} from "@duvi-agency/ui";
import {
  DuviCoreAdminMonoChip as MonoChip,
  DuviCoreAdminSystemIcon as SystemIcon,
} from "../../components/duvi-core-admin-kit";
import {
  duviCoreAdminAuditActionVariant,
  duviCoreAdminNicheStatusPill,
  duviCoreAdminNicheStatusLabel,
} from "../../lib/duvi-core-admin-mappings";
import type { AuditEvent, NicheSummary } from "../../api/client";
import type { DuviCoreAdminKpi } from "./use-duvi-core-admin-dashboard";

export interface DuviCoreAdminDashboardViewProps {
  kpis: DuviCoreAdminKpi[];
  recentAudit: AuditEvent[];
  nicheHealth: NicheSummary[];
  generatedAt: string;
  onNavigate: (path: string) => void;
}

type AuditRow = AuditEvent & Record<string, unknown>;

export function DuviCoreAdminDashboardView({
  kpis,
  recentAudit,
  nicheHealth,
  generatedAt,
  onNavigate,
}: DuviCoreAdminDashboardViewProps) {
  return (
    <div className="grid grid-cols-12 gap-5" data-testid="dashboard-page">
      {kpis.map((k) => (
        <div
          key={k.testId}
          className="col-span-4 max-[900px]:col-span-12 cursor-pointer"
          onClick={() => onNavigate(k.go)}
        >
          <div
            className={
              k.error ? "rounded-lg ring-1 ring-inset ring-error" : undefined
            }
          >
            <KpiCard
              label={k.label}
              icon={<Icon name={k.icon as never} size={18} />}
              value={
                <span
                  data-testid={k.testId}
                  className={k.error ? "text-error" : undefined}
                >
                  {k.value}
                </span>
              }
              footnote={k.footnote}
            />
          </div>
        </div>
      ))}

      <div className="col-span-8 max-[900px]:col-span-12">
        <Card>
          <CardHeader
            title="Recent audit events"
            icon={<Icon name="activity" size={18} />}
            actions={
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onNavigate("/audit-log")}
              >
                View all
              </Button>
            }
          />
          {recentAudit.length === 0 ? (
            <CardBody>
              <div className="py-8 text-center text-body-sm text-text-muted">
                No audit events yet.
              </div>
            </CardBody>
          ) : (
            <DataTable<AuditRow>
              rowKey="event_id"
              data={recentAudit.slice(0, 5) as AuditRow[]}
              columns={[
                {
                  key: "timestamp",
                  header: "Time",
                  sortable: false,
                  render: (r) => (
                    <MonoChip>
                      {r.timestamp.slice(11, 19) || r.timestamp}
                    </MonoChip>
                  ),
                },
                {
                  key: "actor",
                  header: "Actor",
                  sortable: false,
                  render: (r) => {
                    const isSystem = !r.actor_id && !r.actor_name;
                    return (
                      <span className="flex items-center gap-2">
                        {isSystem ? (
                          <span className="text-text-muted">
                            <SystemIcon />
                          </span>
                        ) : (
                          <Avatar name={r.actor_name || r.actor_id} size="xs" />
                        )}
                        {r.actor_name || r.actor_id || "System"}
                      </span>
                    );
                  },
                },
                {
                  key: "action",
                  header: "Action",
                  sortable: false,
                  render: (r) => (
                    <Badge variant={duviCoreAdminAuditActionVariant(r.action)}>
                      {r.action}
                    </Badge>
                  ),
                },
                {
                  key: "entity",
                  header: "Entity",
                  sortable: false,
                  render: (r) => (
                    <span className="text-text-muted">
                      {r.entity_type}
                      {r.entity_name ? ` · ${r.entity_name}` : ""}
                    </span>
                  ),
                },
              ]}
            />
          )}
        </Card>
      </div>

      <div className="col-span-4 max-[900px]:col-span-12">
        <Card>
          <CardHeader
            title="Niche health"
            icon={<Icon name="box" size={18} />}
            actions={
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onNavigate("/platform/niches")}
              >
                View all
              </Button>
            }
          />
          <CardBody>
            {nicheHealth.length === 0 ? (
              <div className="text-body-sm text-text-muted">
                No niches provisioned yet.
              </div>
            ) : (
              <div className="flex flex-col gap-3.5">
                {nicheHealth.slice(0, 5).map((n) => (
                  <div
                    key={n.short_code}
                    className="flex items-center justify-between gap-3"
                  >
                    <span className="font-medium text-body-sm">{n.name}</span>
                    <StatusPill status={duviCoreAdminNicheStatusPill(n.status)}>
                      {duviCoreAdminNicheStatusLabel(n.status)}
                    </StatusPill>
                  </div>
                ))}
              </div>
            )}
          </CardBody>
        </Card>
      </div>

      {generatedAt && (
        <div className="col-span-12 flex items-center gap-2 text-body-sm text-text-muted">
          <Icon name="activity" size={14} /> Last refreshed:{" "}
          {new Date(generatedAt).toLocaleString()}
        </div>
      )}
    </div>
  );
}
